import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
  ReactiveFormsModule
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { confirmResetPassword } from '@aws-amplify/auth';
import { NgxSpinnerService } from 'ngx-spinner';
import { InputComponent } from '../shared/input';
import { LocaleService } from 'src/app/services/locale';
import { ButtonComponent } from '../shared/button';

@Component({
  selector: 'app-reset',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    CommonModule,
    InputComponent,
    ButtonComponent
  ],
  template: `
    <div class="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-6">
      <h1 class="text-xl sm:text-2xl lg:text-3xl font-bold futura text-[#043B22]">
        Reimpostare la password
      </h1>
      <p class="text-xs sm:text-sm text-[#616161]">
        Inserisci la tua nuova password
      </p>
    </div>
    <form [formGroup]="form" (submit)="submit()" class="flex flex-col gap-3 sm:gap-4">
      <app-input
        [keyUp]="reset"
        type="password"
        label="{{ locale.common.lbl_password }}"
        placeholder="{{ locale.common.placeholder_password }}"
        [control]="form.controls['password']"
      ></app-input>
      <app-input
        [keyUp]="reset"
        type="password"
        label="{{ locale.register.lbl_repeat_password }}"
        placeholder="{{ locale.common.placeholder_password }}"
        [control]="form.controls['repeat']"
      ></app-input>
      <div *ngIf="error.length" class="text-red-500 text-xs sm:text-sm bg-red-50 p-3 rounded-lg">
        {{ error }}
      </div>
      <div class="mt-1 sm:mt-2">
        <app-button
          [disabled]="form.invalid"
          type="submit"
          text="Reimpostare la password"
        ></app-button>
      </div>
    </form>
  `
})
export class ResetComponent {
  private router: Router = inject(Router);
  private spinner = inject(NgxSpinnerService);
  private localeService = inject(LocaleService);

  locale = this.localeService.locale();

  error = '';
  username = '';
  otp = '';

  navigation = this.router.getCurrentNavigation();

  form = new FormGroup(
    {
      password: new FormControl('', [Validators.required]),
      repeat: new FormControl('', [Validators.required])
    },
    {
      validators: this.passwordsMatchValidator
    }
  );

  constructor() {
    // Try to get from navigation state first, then fallback to sessionStorage
    this.username = this.navigation?.extras?.state?.['username'] || sessionStorage.getItem('resetPasswordUsername') || '';
    this.otp = this.navigation?.extras?.state?.['otp'] || sessionStorage.getItem('resetPasswordOtp') || '';

    // Redirect to forgot password if no username/otp available
    if (!this.username || !this.otp) {
      this.router.navigate(['/forgot']);
    }
  }

  passwordsMatchValidator(control: AbstractControl) {
    const password = control.get('password')?.value;
    const repeat = control.get('repeat')?.value;

    if (password !== repeat) {
      control.get('repeat')?.setErrors({ passwordMismatch: true });
    } else {
      control.get('repeat')?.setErrors(null);
    }

    return null;
  }

  submit = async () => {
    this.spinner.show();

    try {
      const credentials = {
        newPassword: this.form.controls.password.value as string,
        username: this.username,
        confirmationCode: this.otp
      };

      await confirmResetPassword(credentials);

      // Clean up sessionStorage after successful password reset
      sessionStorage.removeItem('resetPasswordUsername');
      sessionStorage.removeItem('resetPasswordOtp');

      this.spinner.hide();

      this.router.navigateByUrl('/login');
    } catch (error) {
      this.spinner.hide();
      this.error = (error as any).message;
    }
  };

  reset = () => {
    this.error = '';
  };
}
